import React from 'react';
import {
  XAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  ReferenceLine,
  Dot,
} from 'recharts';
import AxisY from '../AxisY';
import LegendContent from '../LegendContent';
import TooltipContent from '../TooltipContent';
import ReferenceLineLabel from '../ReferenceLineLabel';
import AnnotationLineIcon from '../AnnotationLineIcon';
import formatData from '../../methods/formatData';

const LineChartContent = ({
  framed,
  data,
  seriesConfig,
  dateOnXAxis,
  labelX,
  labelSeries,
  syncId,
  compareMode,
  lineColors,
  t,
  disableAnimations,
  dotsOnly,
  referenceLine,
  referenceLineVertical,
  showWhiteSpace,
  selectedAnnotationName,
  onClick,
  withAnnotations,
  smallerTooltip,
  hideTooltip,
  withNowElement,
  chartId,
  clickableLegendProps,
}) => (
  <LineChart
    data={data}
    margin={{
      top: framed ? 26 : 0,
      right: seriesConfig.find(serie => (serie.axisY === 'right')) ? 0 : 24,
      left: 0,
      bottom: labelX ? 26 : 8,
    }}
    syncId={syncId ? `${syncId}_${data.length}` : undefined}
    onClick={onClick}
  >
    <XAxis
      dataKey="name"
      fontSize="10px"
      stroke="#131313"
      tickLine={false}
      tickMargin={7}
      tickFormatter={tick => formatData(tick, dateOnXAxis, false, false, t)}
      label={{
        value: labelX,
        position: 'insideBottom',
        fontSize: '10px',
        offset: -9,
      }}
    />
    {AxisY({ seriesConfig, isOnRight: false, t })}
    {AxisY({ seriesConfig, isOnRight: true, t })}
    <CartesianGrid stroke="#EEEEEE" strokeWidth={1} vertical={false} />
    {!hideTooltip && (
      <Tooltip
        content={p => TooltipContent({
          ...p,
          dateOnXAxis,
          compareMode,
          compareDivider: 1,
          t,
          chartType: dotsOnly && 'dots',
          showWhiteSpace,
          smallerTooltip,
        })}
      />
    )}
    <Legend
      content={p => LegendContent({
        ...p,
        framed,
        labelSeries,
        compareMode,
        compareDivider: 1,
        chartType: dotsOnly ? 'dots' : 'line',
        clickableLegendProps,
      })}
      verticalAlign="top"
      wrapperStyle={{ width: '100%', top: '0' }}
    />
    {seriesConfig.map((serie, index) => {
      const serieColor = serie.color
        ? lineColors.filter(item => item.name === serie.color)[0].value
        : lineColors[index % lineColors.length].value;

      const nowBreakpoint = ((data.length - 2) * 100) / (data.length - 1);

      const dot = (props) => {
        const { cx, cy, r, key, index: dotIndex } = props;

        if (dotsOnly) {
          return <Dot cx={cx} cy={cy} r={r} strokeWidth={3} fill={serieColor} stroke={serieColor} key={key} />;
        }

        if (withNowElement && dotIndex === data.length - 1) {
          return <Dot cx={cx} cy={cy} r={r + 1} strokeWidth={2} fill="#FFFFFF" stroke={serieColor} key={key} />;
        }

        if (withNowElement && dotIndex === data.length - 2) {
          return <Dot cx={cx} cy={cy} r={r} strokeWidth={3} fill={serieColor} stroke={serieColor} key={key} />;
        }

        return false;
      };

      return (
        <>
          <defs>
            <linearGradient id={`gradient_${chartId}_${serieColor}`} x1="0%" y1="0" x2="100%" y2="0">
              <stop offset="0%" stopColor={serieColor} />
              <stop offset={`${nowBreakpoint}%`} stopColor={serieColor} />
              <stop offset={`${nowBreakpoint}%`} stopColor="#DDDDDD" />
              <stop offset="100%" stopColor="#FAFAFA" />
            </linearGradient>
          </defs>
          <Line
            yAxisId={serie.axisY}
            type="linear"
            color={serieColor}
            dataKey={serie.id}
            name={serie.name}
            unit={serie.unit}
            stroke={withNowElement ? `url(#gradient_${chartId}_${serieColor})` : serieColor}
            dot={dot}
            activeDot={
              dotsOnly
                ? {
                  stroke: serieColor,
                  strokeWidth: 3,
                  fill: serieColor,
                }
                : {
                  fill: serieColor,
                }
            }
            strokeWidth={dotsOnly ? 0 : 2}
            key={serie.id}
            isAnimationActive={!disableAnimations && !dotsOnly}
          />
        </>
      );
    })}
    {
      referenceLine && (Array.isArray(referenceLine) ? referenceLine : [referenceLine]).map(el => (
        <ReferenceLine
          key={el.value}
          y={el.value}
          label={<ReferenceLineLabel label={el.label} />}
          stroke="#131313"
          strokeDasharray="3 1"
          yAxisId="left"
        />
      ))
    }
    {
      referenceLineVertical && (Array.isArray(referenceLineVertical) ? referenceLineVertical : [referenceLineVertical]).map(el => (
        <ReferenceLine
          key={el.value}
          x={el.name}
          label={<ReferenceLineLabel label={el.label} isVertical />}
          stroke="#131313"
          strokeDasharray="3 1"
          yAxisId="left"
        />
      ))
    }
    {
      [
        ...data.filter(el => el.numberOfAnnotations > 0 && selectedAnnotationName !== el.name),
        ...data.filter(el => el.numberOfAnnotations > 0 && selectedAnnotationName === el.name), // Selected on top
      ]
        .map(el => (
          withAnnotations ? (
            <ReferenceLine
              key={el.name}
              x={el.name}
              label={<AnnotationLineIcon selected={selectedAnnotationName === el.name} />}
              stroke="#CCCCCC"
              opacity={Number(selectedAnnotationName === el.name)}
              yAxisId="left"
            />
          ) : null
        ))
    }
  </LineChart>
);

export default LineChartContent;
